import {
  Alert,
  Button,
  Checkbox,
  Flex,
  IconNotepadLine,
  IconUploadLine,
  Link,
  Spinner,
  Text,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useState,
} from "react";
import Skeleton from "react-loading-skeleton";
import { Link as RouterLink } from "react-router-dom";
import { CourseService } from "../../../services/CourseService";
import { QualityCheckService } from "../../../services/QualityCheckService";
import { ReflectionTemplateService } from "../../../services/ReflectionTemplateService";
import { ReviewService } from "../../../services/ReviewService";
import { TaskService } from "../../../services/TaskService";
import { TaxonomyService } from "../../../services/TaxonomyService";
import {
  FeatureContext,
  LoadingContext,
  MasteryLevelContext,
  UserDetailContext,
} from "../../../utils/Context";
import { waitForExpectedResult } from "../../../utils/Timer";
import { RefreshMetricsContext } from "../../../views/Outcomes";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../CustomHooks/NotificationProvider";
import {
  FAIL,
  GOOD,
  useStatus,
  WORKING,
} from "../../CustomHooks/StatusProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import CourseReviewCyclesModal from "./CourseReviewCycles/CourseReviewCyclesModal";
import CourseToggle from "./Outcomes/CourseToggle";
import QualitySettingsModal from "./QualitySettingsModal/QualitySettingsModal";
import BulkCLOUploadModal from "./UploadModals/BulkCLOUploadModal";
import "./rubric.css";
import { sortCourses } from "./utils";

export const OutcomesContext = createContext();
export const ReviewCycleContext = createContext();

export const Review = memo(
  ({
    filters,
    trigger,
    setTrigger,
    subLoad,
    setDisableTaxFilter,
    setDisableLengthFilter,
    departments,
    courseLevels,
  }) => {
    const showNotification = useNotification();
    const { setRefreshMetrics, setSubjects } = useContext(
      RefreshMetricsContext
    );
    const { updateStatus, toggleStatus } = useStatus();
    const userDetails = useContext(UserDetailContext);
    const loadingDetails = useContext(LoadingContext);
    const { ratingScales } = useContext(MasteryLevelContext);
    const features = useContext(FeatureContext);
    const [courses, setCourses] = useState([]);
    const [taxonomyLevels, setTaxonomyLevels] = useState([]);
    const [loadingOutcomes, setLoadingOutcomes] = useState(false);
    const [loadingTaxonomy, setLoadingTaxonomy] = useState(false);
    const [openSettingsModal, setOpenSettingsModal] = useState(false);
    const [loadingSaveSettings, setLoadingSaveSettings] = useState(false);
    const [qcEnabled, setQcEnabled] = useState(false);
    const [comparisonType, setComparisonType] = useState(null);
    const [currentQcConfig, setCurrentQcConfig] = useState({});
    const [error, setError] = useState(false);

    const [qcConfig, setQcConfig] = useState({
      id: "",
      titleMin: "",
      titleMax: "",
      descriptionMin: "",
      descriptionMax: "",
      noPolicyChecked: false,
      noVariantPolicyChecked: false,
      atLeastChecked: false,
      maxChecked: false,
      leastNumVerbs: "",
      maxNumVerbs: "",
      percentDifference: "",
      comparisonType: "",
      description: "",
      lastUpdated: "",
      ai_enabled: false,
    });
    const [approveAllPending, setApproveAllPending] = useState(false);
    const [approveAllOpen, setApproveAllOpen] = useState(false);
    const [privateAllOpen, setPrivateAllOpen] = useState(false);
    const [privateAllPending, setPrivateAllPending] = useState(false);

    const [unmappedRatingScale, setUnmappedRatingScale] = useState([]);
    const [reflectionTemplates, setReflectionTemplates] = useState([]);
    const [courseReviewCycles, setCourseReviewCycles] = useState(null);

    const finalizeFlag =
      features.find((f) => f.name === "finalize_results_flag")?.value ===
      "TRUE";

    useEffect(() => {
      async function fetchTemplatesAndCycles() {
        const [templates, cycles] = await Promise.all([
          ReflectionTemplateService.getTemplates(),
          ReviewService.getCycles(),
        ]);
        setReflectionTemplates(templates.map((t) => ({ ...t, label: t.name })));
        cycles.forEach((cycle) => {
          cycle.reviewables.forEach((reviewable) => {
            reviewable.id = reviewable.ddl_id;
            reviewable.code = reviewable.name;
          });
        });
        setCourseReviewCycles(cycles);
      }
      fetchTemplatesAndCycles();
    }, []);

    useEffect(() => {
      if (trigger && userDetails.role && filters.subject) {
        setTrigger(false);
        setLoadingOutcomes(true);
        const courseSort = {
          ownerCourseCode: null,
          programIds: null,
          courseGroupIds: null,
          courseIds: null,
        };
        if (filters.programs.length === 0) {
          courseSort.ownerCourseCode = filters.subject;
        }
        if (
          filters.programs.length > 0 &&
          filters.courseGroups.length === 0 &&
          filters.courses.length === 0
        ) {
          courseSort.programIds = filters.programs;
        }
        if (filters.courseGroups.length > 0 && filters.courses.length === 0) {
          courseSort.courseGroupIds = filters.courseGroups;
        }
        CourseService.getCourses({
          ownerCourseCode: courseSort.ownerCourseCode,
          programIds: courseSort.programIds,
          courseGroupIds: courseSort.courseGroupIds,
          courseIds: filters.courses,
          courseLevels: filters.courseLevels,
          taxonomyLevels: filters.taxonomyLevels,
          qc: qcEnabled,
          approval: filters.outcomeFilters.approval,
          verb_count: filters.outcomeFilters.verbCount,
          outcome_length: filters.outcomeFilters.outcomeLength,
          withOutcomes: true,
        }).then((data) => {
          const sortedCourses = sortCourses(data.data);
          setCourses(sortedCourses);
          setLoadingOutcomes(false);
        });
      }
    }, [filters, trigger, setTrigger, userDetails]);

    useEffect(() => {
      if (qcEnabled) {
        setTrigger(true);
      }
    }, [qcEnabled]);

    useEffect(() => {
      if (ratingScales) {
        setUnmappedRatingScale(
          ratingScales.filter((map) => map.is_canvas_default && !map.is_mapped)
        );
      }
    }, [ratingScales]);

    useEffect(() => {
      setLoadingTaxonomy(true);
      TaxonomyService.getAllTaxonomies().then((taxonomies) => {
        const levels = [];
        for (const taxonomy of taxonomies) {
          const taxLevels = Object.values(taxonomy.objectives);
          taxLevels.sort((a, b) => a.sequence - b.sequence);
          levels.push(...taxLevels);
        }
        const taxonomyLevelOptions = levels.map((level) => {
          return { ...level, label: level.title };
        });
        setTaxonomyLevels(taxonomyLevelOptions);
        setLoadingTaxonomy(false);
      });
      QualityCheckService.getOutcomeConfiguration().then((config) => {
        disableOutcomeFiltersCheck(config);
      });
    }, []);

    const disableOutcomeFiltersCheck = (data) => {
      setQcConfig({
        id: data.id,
        titleMin: data.titleMin ?? "",
        titleMax: data.titleMax ?? "",
        descriptionMin: data.descriptionMin ?? "",
        descriptionMax: data.descriptionMax ?? "",
        noPolicyChecked: data.noPolicyChecked,
        noVariantPolicyChecked: true,
        atLeastChecked: data.atLeastChecked,

        maxChecked: data.maxChecked,
        leastNumVerbs: data.leastNumVerbs ?? "",
        maxNumVerbs: data.maxNumVerbs ?? "",
        percentDifference: "",
        comparisonType: data.comparisonType || "",
        description: data.description || "",
        lastUpdated: data.lastUpdated || "",
        ai_enabled: data.ai_enabled,
      });
      setDisableTaxFilter(
        !(
          !data.noPolicyChecked ||
          (data.atLeastChecked && data.leastNumVerbs) ||
          (data.maxChecked && data.maxNumVerbs)
        )
      );
      setDisableLengthFilter(
        !(
          data.titleMin &&
          data.titleMax &&
          data.descriptionMax &&
          data.descriptionMin
        )
      );
    };

    const handleOpenSettingsModal = () => {
      setOpenSettingsModal(true);
      setCurrentQcConfig({ ...qcConfig });
    };

    const handleCloseSettingsModal = () => {
      setOpenSettingsModal(false);
      setQcConfig({ ...currentQcConfig });
    };

    const handleSaveSettings = () => {
      setLoadingSaveSettings(true);
      const inputs = inputCheck();
      if (!inputs) {
        setLoadingSaveSettings(false);
      } else if (!qcConfig.id) {
        QualityCheckService.createOutcomeConfiguration(qcConfig)
          .then(() => {
            setOpenSettingsModal(false);
            setTrigger(true);
            setLoadingSaveSettings(false);
            setDisableLengthFilter(false);
            setDisableTaxFilter(false);
            showNotification(t("notifications.analyze.createConfigSuccess"));
          })
          .catch((e) => {
            showNotification(
              `${t("notifications.analyze.createConfigError")} ${e}`,
              ERROR
            );
          });
      } else {
        QualityCheckService.updateOutcomeConfiguration(qcConfig.id, qcConfig)
          .then((data) => {
            if (
              currentQcConfig.noVariantPolicyChecked !==
                qcConfig.noVariantPolicyChecked ||
              currentQcConfig.percentDifference !==
                qcConfig.percentDifference ||
              currentQcConfig.comparisonType !== qcConfig.comparisonType
            ) {
              loadingDetails.setLoadingTriggered(true);
            } else {
              showNotification(t("notifications.analyze.updateConfigSuccess"));
            }
            disableOutcomeFiltersCheck(data);
            setOpenSettingsModal(false);
            setTrigger(true);
            setLoadingSaveSettings(false);
          })
          .catch((e) => {
            showNotification(
              `${t("notifications.analyze.updateConfigError")} ${e}`,
              ERROR
            );
          });
      }
    };

    const inputCheck = () => {
      const checkValidity = (value) => {
        return !isNaN(value) && value >= 0 && value % 1 === 0;
      };
      const isValid =
        checkValidity(qcConfig.titleMax) &&
        checkValidity(qcConfig.titleMin) &&
        checkValidity(qcConfig.descriptionMax) &&
        checkValidity(qcConfig.descriptionMin) &&
        checkValidity(qcConfig.percentDifference) &&
        checkValidity(qcConfig.maxNumVerbs) &&
        checkValidity(qcConfig.leastNumVerbs);
      setError(!isValid);
      return isValid;
    };

    const processClassifyAllOutcomesTask = (task) => {
      if (task.status === "done") {
        setTrigger(true);
        setRefreshMetrics(true);
        setSubjects((prevSubjects) =>
          prevSubjects.map((subject) => {
            if (subject.title === filters.subject) {
              return {
                ...subject,
                unapproved_outcome_count:
                  subject.unapproved_outcome_count - task.obj.total_outcomes,
                renderAfterLabel: (
                  <Text size="small" fontStyle="italic">{`${
                    subject.unapproved_outcome_count - task.obj.total_outcomes
                  } ${t("analyze.unapproved")}`}</Text>
                ),
              };
            }
            return subject;
          })
        );
        showNotification(
          t("notifications.analyze.classifyingOutcomeComplete"),
          SUCCESS
        );
      } else if (task.status === "error") {
        showNotification(
          t("notifications.analyze.classifyingOutcomeFail"),
          ERROR
        );
        updateStatus({
          service: { name: t("status.approvalStatus"), status: FAIL },
        });
      }
      setApproveAllPending(false);
      setPrivateAllPending(false);
    };

    const classifyAllOutcomes = (classification) => {
      showNotification(
        <Text>
          {t("notifications.analyze.classifyingOutcomeProgress")}
          <Link
            themeOverride={{ fontWeight: "bold" }}
            onClick={() => toggleStatus(true)}
          >
            {t("status.checkStatus")}
          </Link>
        </Text>,
        SUCCESS,
        10000
      );
      updateStatus({
        service: {
          name: t("status.approvalStatus"),
          status: WORKING,
          progress: 0,
        },
      });

      return CourseService.approveAllOutcomes(
        filters.subject,
        filters.courseLevels,
        filters.taxonomyLevels,
        filters.outcomeFilters.verbCount,
        filters.outcomeFilters.outcomeLength,
        classification
      ).then((task) => {
        waitForExpectedResult(
          () => TaskService.getTaskById(task.ddl_id),
          (task) => {
            const progress = parseInt(
              (task.counter / task.obj.total_outcomes) * 100
            );
            updateStatus({
              service: {
                name: t("status.approvalStatus"),
                status: progress === 100 ? GOOD : WORKING,
                progress: progress,
              },
            });
            return task.status === "done" || task.status === "error";
          },
          processClassifyAllOutcomesTask,
          () => {
            showNotification(
              t("notifications.analyze.classifyingOutcomeFail"),
              ERROR
            );
            updateStatus({
              service: { name: t("status.approvalStatus"), status: FAIL },
            });
            setApproveAllPending(false);
            setPrivateAllPending(false);
          },
          // TODO: Decrease retry count as some point, too many retries in event of an error
          200,
          3000
        );
      });
    };

    const handleSelect = (id) => {
      setComparisonType(id);
      setQcConfig((prevConfig) => ({ ...prevConfig, comparisonType: id }));
    };

    return (
      <ReviewCycleContext.Provider
        value={{
          departments,
          courseLevels,
          reflectionTemplates,
          courseReviewCycles,
          setCourseReviewCycles,
        }}
      >
        <Flex gap="small" padding="small 0">
          {finalizeFlag && (
            <Flex.Item shouldGrow>
              <ManageCyclesButton />
            </Flex.Item>
          )}
          <Flex.Item shouldGrow={!finalizeFlag} textAlign="end">
            <BulkUploadButton />
          </Flex.Item>
          {userDetails.isAC() && qcEnabled && (
            <Flex.Item>
              <Button
                id="open-quality-settings"
                size="small"
                onClick={handleOpenSettingsModal}
              >
                {t("analyze.editQualitySettings")}
              </Button>
            </Flex.Item>
          )}
          <Flex.Item>
            <Checkbox
              label={t("review.qualityCheck")}
              labelPlacement="start"
              variant="toggle"
              checked={qcEnabled}
              onChange={() => {
                setQcEnabled(!qcEnabled);
              }}
            />
          </Flex.Item>
        </Flex>
        {userDetails.role && !loadingOutcomes && subLoad && !courses.length && (
          <Alert variant="info">{t("analyze.noCoursesAvailable")}</Alert>
        )}
        <div
          style={{
            border:
              !loadingOutcomes && !courses.length ? "hidden" : "1px solid #ccc",
            borderRadius: "5px",
            padding: 10,
          }}
        >
          {!!courses.length && (
            <Flex justifyItems="end" gap="small" margin="0 0 small 0">
              <Flex.Item>
                <Button
                  color="secondary"
                  size="small"
                  onClick={() => setPrivateAllOpen(true)}
                  interaction={
                    approveAllPending || privateAllPending
                      ? "disabled"
                      : "enabled"
                  }
                >
                  {privateAllPending ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : (
                    <Text size="small">{t("analyze.setAllPrivate")}</Text>
                  )}
                </Button>
              </Flex.Item>
              <Flex.Item>
                <Tooltip
                  color="primary-inverse"
                  renderTip={
                    unmappedRatingScale.length > 0 ? (
                      <div>
                        {t("analyze.denyApproveAllTooltipText")}{" "}
                        <RouterLink to="/setup/mastery">
                          {t("analyze.denyApproveAllTooltipLinkText")}
                        </RouterLink>
                      </div>
                    ) : (
                      t("analyze.approveAllTooltipText")
                    )
                  }
                >
                  <Button
                    color="primary"
                    size="small"
                    onClick={() => setApproveAllOpen(true)}
                    interaction={
                      unmappedRatingScale.length > 0 ||
                      approveAllPending ||
                      privateAllPending
                        ? "disabled"
                        : "enabled"
                    }
                  >
                    {approveAllPending ? (
                      <Spinner
                        renderTitle={t("global.pleaseWait")}
                        size="x-small"
                      />
                    ) : (
                      <Text size="small">{t("analyze.approveAll")}</Text>
                    )}
                  </Button>
                </Tooltip>
              </Flex.Item>
            </Flex>
          )}
          <OutcomesContext.Provider
            value={{ setCourses, taxonomyLevels, qcEnabled, qcConfig }}
          >
            {loadingOutcomes || loadingTaxonomy || !subLoad ? (
              <Skeleton
                count={3}
                style={{ height: 46, borderRadius: 5, marginBottom: 15 }}
              />
            ) : (
              courses.map((course) => (
                <CourseToggle key={course.id} course={course} />
              ))
            )}
          </OutcomesContext.Provider>
          <QualitySettingsModal
            openModal={openSettingsModal}
            handleClose={handleCloseSettingsModal}
            qcConfig={qcConfig}
            id={qcConfig.comparisonType}
            setQcConfig={setQcConfig}
            handleSave={handleSaveSettings}
            selectHandler={handleSelect}
            error={error}
            loadingSaveSettings={loadingSaveSettings}
            selectedId={comparisonType}
            interaction={!qcConfig.noVariantPolicyChecked}
          />
          <DialogYesNo
            message={t("analyze.approveAllConfirm")}
            openStatus={approveAllOpen || privateAllOpen}
            onClickYes={() => {
              if (approveAllOpen) {
                setApproveAllPending(true);
                classifyAllOutcomes("new");
              } else {
                setPrivateAllPending(true);
                classifyAllOutcomes("private");
              }
              setApproveAllOpen(false);
              setPrivateAllOpen(false);
            }}
            onClickNo={() => {
              setApproveAllOpen(false);
              setPrivateAllOpen(false);
            }}
          />
        </div>
      </ReviewCycleContext.Provider>
    );
  }
);

const BulkUploadButton = () => {
  const [uploadDialog, setUploadDialog] = useState(false);

  return (
    <>
      <Button
        color="primary"
        size="small"
        renderIcon={IconUploadLine}
        onClick={() => setUploadDialog(true)}
      >
        {t("review.bulkCLOUpload")}
      </Button>
      <BulkCLOUploadModal
        uploadDialog={uploadDialog}
        setUploadDialog={setUploadDialog}
      />
    </>
  );
};

const ManageCyclesButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        size="small"
        renderIcon={IconNotepadLine}
        onClick={() => setOpen(true)}
      >
        {t("courseReviewCycle.manageCycles")}
      </Button>
      <CourseReviewCyclesModal open={open} setOpen={setOpen} />
    </>
  );
};

export default Review;
